import { FC, useEffect, useState, useRef, MouseEvent, TouchEvent } from "react";
import { Id, TableObject, CustomCSSProperties, RectKey, BookingTable, BookingProduct, LayoutLocation, SafeZone, ZonesAvailabilityCache, GroupedTables, FocusRectOptions } from "../../types";
import { DEFAULT_BOOKING_INSTRUCTIONS, DEFAULT_DIALOG_HANDLE, DEFAULT_EVENT_AVAILABILITY, SCREEN_BREAKPOINT_2, SURCHARGE_NAME, TABLE_MAP_ROTATIONS, timeOuts } from "../../utils/symbols";
import { Icon } from "../../parts/Icon";
import { useApp } from "../../context/useApp";
import { Dialog } from "../dialog/Dialog";
import { rumba } from "../../utils/symbols";
import { Table } from "./Table";
import { processTableLayout, _, getZoneName, parseNum, getTableVars, load, save } from "../../utils/helpers";
import { Stage } from "./Stage";
import { useTableLayoutRefresh } from "../../hooks/useTableLayoutRefresh";

interface TableLayoutProps {
    onSeatClick: (table: BookingTable, product: BookingProduct | undefined, layoutLocation: LayoutLocation) => void
}



export interface TableLayoutHandle {
    focusRect: (options?:FocusRectOptions) => void
    goToStartPosition: () => void
    zoomOut: (modifier?: number) => void
    zoomIn: (modifier?: number) => void
    getEventOccupation: (zone?: SafeZone) => {max: number, occupied: number, available: number, percentAvailable: number, percentOccupied: number}
    searchForIndividualTables: () => void
    searchForDoubleTables: () => void
    searchForAccessibleTables: () => void
    getCurrentlyFocusing: () => string | undefined
    removeFocusStyles: () => void,
    selectAdjacentChildrenSeat: () => void
}

export const TableLayout:FC<TableLayoutProps> = ({onSeatClick}) => {

    const {
        state: { shifts, booking, dialogHandles, forcedRenders, screen, products, venueData, layout },
        actions: { updateTableMapHandle, genericError, forceRender, updateLayout }
    } = useApp()

    const tableLayout = useTableLayoutRefresh()
    const lastEventIdRequested = useRef<Id>(0)
    const container = useRef<HTMLDivElement>(null)
    const zoomControler = useRef<HTMLDivElement>(null)
    const zoom = useRef<number>(1)
    const mouseIsDown = useRef<boolean>(false)
    const startPoint = useRef({x: 0, y: 0})
    const lastBookingStep = useRef('')
    const lastClick = useRef(0)
    const lastDistance = useRef(0)
    const bookedZones = booking.getZonesOccupancy()
    const dialog = dialogHandles.get('table-map-dialog') ?? DEFAULT_DIALOG_HANDLE

    const LAYOUT_ORIGIN_X = layout.rects.cv.width / 2 + layout.rects.cv.lt.x
    const LAYOUT_ORIGIN_Y= layout.rects.cv.height/ 2 + layout.rects.cv.lt.y
    const CONTAINER_CENTER_X = (container.current?.offsetWidth ?? 0) / 2 - layout.rects.cv.width / 2 - layout.rects.cv.lt.x
    const CONTAINER_CENTER_Y = (container.current?.offsetHeight ?? 0) / 2 - layout.rects.cv.height / 2 - layout.rects.cv.lt.y

    const getTableLayout = async (eventId: number):Promise<void> => {

        if ((eventId === 0 || lastEventIdRequested.current === eventId) || !venueData) return
        lastEventIdRequested.current = eventId

        await tableLayout.refresh(eventId)
    }

    const focusRect = ({rect, animate, dataFocusing}:FocusRectOptions = {rect: 'cv', animate: true}) => {
        
        if (container.current) {
            
            let ref = layout.rects[rect],s = 1, rx = 1, ry = 1
            let px = window.innerWidth > SCREEN_BREAKPOINT_2 ? 25 : 10 
            let py = window.innerWidth > SCREEN_BREAKPOINT_2 ? 25 : 10

            let mw = container.current.offsetWidth - px * 2
            let mh = container.current.offsetHeight - py * 2
            let cw = ref.width
            let ch = ref.height
            let x = (-(ref.lt.x - LAYOUT_ORIGIN_X) - ref.width / 2)
            let y = (-(ref.lt.y - LAYOUT_ORIGIN_Y) - ref.height / 2)

            if (layout.appliedRotation === 90) {
                [mw, mh] = [mh, mw];
                [cw, ch] = [cw, ch];
                [x, y] = [y, x];
                ry = 1
                rx = -1
            } else if (layout.appliedRotation === 180) {
                rx = -1
                ry = -1
            } else if (layout.appliedRotation === 270) {
                [mw, mh] = [mh, mw];
                [cw, ch] = [cw, ch];
                [x, y] = [y, x];
                ry = -1
                rx = 1
            }

            s = cw * (mh / ch) <= mw ? mh / ch : mw / cw
            x *= rx; y *= ry
    
            applyZoom(s, false)
            zoomControler.current!.style.transform = `translate(${x}px, ${y}px)`

            container.current.setAttribute('data-focusing', dataFocusing ?? rect)
            booking.render()
            
            if (animate || animate === undefined) {
                container.current.classList.add('with-transition')
                timeOuts.set('focusRect', () => container.current?.classList.remove('with-transition'), 1000)//css animation time
            }
        }
    }
    
    const getEventOccupation = (zone?: SafeZone) => {

            let max = 0
            let occupied = 0

            for (let table of layout.processedTables) {

                if (zone && table.zone !== zone) continue

                max += table.pax
                if (!!table.occupied) occupied += table.pax
            }

            const shift = shifts.find(shift => shift.id === booking.shiftId) ?? shifts[0]
            if (
                max === 0 ||
                (shift && ((zone && shift[`${zone}Open` as keyof typeof shift] === 0) || shift.webOpen === 0))
            ) 
                //if zone or event closed or tables are not loaded yet, return an event 100% occupied
                return {max: 0, occupied: 0, available: 0,percentAvailable: 0, percentOccupied: 1}

            //normal response
            return {
                max, 
                occupied, 
                available: max - occupied,
                percentAvailable: 1 - occupied / max, 
                percentOccupied: occupied / max
            }
    }

    const handleStart = (clientX:number, clientY:number) => {

        mouseIsDown.current = true
        const style = window.getComputedStyle(zoomControler.current!)
        const match = style.transform.match(/matrix.*\((.+)\)/)
        let x = 0, y = 0
    
        if (match) {
            const matrix = match[1].split(', ')
            x = clientX - parseFloat(matrix[4]) * zoom.current
            y = clientY - parseFloat(matrix[5]) * zoom.current
        }
    
        startPoint.current = {x, y} 
    }

    const handleMouseDown = (event: MouseEvent) => {

        const now = Date.now()
        if (now - lastClick.current < 300) focusRect()
        lastClick.current = now

        handleStart(event.clientX, event.clientY)

    }

    const handleTouchStart = (event: TouchEvent) => {
        const touch1 = event.touches[0]
        if (event.touches.length > 1) {

            const touch2 = event.touches[1];
            const distance = Math.sqrt(
                Math.pow(touch2.clientX - touch1.clientX, 2) +
                    Math.pow(touch2.clientY - touch1.clientY, 2)
            );

            lastDistance.current = distance;
        } else {
            const now = Date.now()
            if (now - lastClick.current < 300) focusRect()
            lastClick.current = now
            handleStart(touch1.clientX, touch1.clientY)
        }
    }

    const handleMove = (clientX: number, clientY: number) => {
        if (!mouseIsDown.current) return

        const softness = 1 / zoom.current
        const diffToMove = 0

        if (
            Math.abs(clientX - startPoint.current.x) < diffToMove ||
            Math.abs(clientY - startPoint.current.y) < diffToMove
        ) return

        const x = (clientX - startPoint.current.x) * softness
        const y = (clientY - startPoint.current.y) * softness

        zoomControler.current!.style.transform = `translate(${x}px, ${y}px)`
    }

    const handleMouseMove = (event: MouseEvent) => handleMove(event.clientX, event.clientY)

    const handleTouchMove = (event: TouchEvent) => {

        const touch1 = event.touches[0];
        
        //ensure we do not have transition enabled
        container.current?.classList.remove('with-zoom-transition', 'with-transition')

        if (event.touches.length > 1) {

            const touch2 = event.touches[1];
            const distance = Math.sqrt(
                Math.pow(touch2.clientX - touch1.clientX, 2) +
                    Math.pow(touch2.clientY - touch1.clientY, 2)
            );
    
            const zoomFactor = distance / lastDistance.current;

            // Aplica el zoom en función del factor calculado
            if (zoomFactor !== 1) {
                applyZoom(zoom.current * zoomFactor, false); // Escala proporcionalmente
            }

            // Actualiza la distancia para el siguiente cálculo
            lastDistance.current = distance;
        } else handleMove(touch1.clientX, touch1.clientY)
    }

    const handleMouseUp = () => mouseIsDown.current = false
    const applyZoom = (newZoom: number, animate: boolean = true) => {
        zoom.current = Math.min(3, Math.max(0.15, newZoom))
        zoomControler.current!.style.scale = String(zoom.current)
        timeOuts.set('applyZoom', () => container.current?.classList.remove('with-zoom-transition'), 500)//css animation time
        if (!animate) return
        container.current!.classList.add('with-zoom-transition')
    }

    const selectAdjacentChildrenSeat = () => {

        const getFreeSeat = (table: BookingTable) => Array.from({length: table.pax}, (_, i) => i + 1)
            .find(seat => !table.seats.includes(seat))!

        const checkTable = (table: TableObject, defaultSeat: number): LayoutLocation | undefined => {

            const tableInBooking = booking.getTable(table.id)
            if (table.occupied) return
            if (tableInBooking && tableInBooking.pax === tableInBooking.occupied) return
            return [
                table.id, 
                tableInBooking ? getFreeSeat(tableInBooking) : defaultSeat
            ]
        }

        const layoutLocations = booking.getTickets({category: 'adult'})
            .flatMap(ticket => ticket.layoutLocations ?? [])
            .sort((a, b) => {

                const tableInBookingA = booking.getTable(a[0])
                const tableInBookingB = booking.getTable(b[0])
                //check first the table in booking that are not fully occupied
                if (tableInBookingA && tableInBookingA.pax > tableInBookingA.occupied) return -1
                if (tableInBookingB && tableInBookingB.pax > tableInBookingB.occupied) return 1
            
                return 0
            })

        const checkedRows: Map<string, any> = new Map()
        let result

        for (const location of layoutLocations) {

            if (result) break

            const [tableId, seat] = location
            //get origin
            const originTable = layout.processedTables.find(({ id }) => id === tableId)
            if (!originTable) throw new Error('table not found in processed tables')
            if (checkedRows.has(`${originTable.side}-${originTable.row}`)) continue
            //get tables of the origin row and ensur cols are in correct order
            const tablesInRow = layout.processedTables.filter(table => 
                table.side === originTable.side &&
                table.row === originTable.row
            ).sort((a, b) => a.col - b.col) // ensure the row is in correct order
            //get the index of the origin and initialize pointers
            const indexOfOrigin = tablesInRow.findIndex(tableInRow => tableInRow.id === originTable.id)
            if (indexOfOrigin === -1) throw new Error('index of origin not found')
            let pointer_a: number | null = indexOfOrigin
            let pointer_b: number | null = indexOfOrigin
            do {
                let result_a, result_b
                //check pointer a
                if (pointer_a !== null && pointer_a >= 0 && pointer_a < tablesInRow.length) {
                    result_a = checkTable(tablesInRow[pointer_a], tablesInRow[pointer_a].pax)
                }
                //check pointer b
                if (pointer_b !== null && pointer_b >= 0 && pointer_b < tablesInRow.length) {
                    result_b = checkTable(tablesInRow[pointer_b], 1)
                }
                //if a & b find a result, keep the best result
                if (result_a && result_b) {
                    const {score: score_a} = tablesInRow[pointer_a!]
                    const {score: score_b} = tablesInRow[pointer_b!]
                    result = score_a > score_b ? result_a : result_b
                } else if (result_a) {
                    result = result_a
                } else if (result_b) {
                    result = result_b
                }   
                //delete pointers if outside array | the pointer table is occupied
                //a
                if (pointer_a !== null && pointer_a < 0) pointer_a = null
                if (pointer_a !== null && tablesInRow[pointer_a].occupied) pointer_a = null
                //b
                if (pointer_b !== null && pointer_b > tablesInRow.length - 1) pointer_b = null
                if (pointer_b !== null && tablesInRow[pointer_b].occupied) pointer_b = null
                //break
                if (pointer_a === null && pointer_b === null) break
                if (result) break
                //iterate
                if (pointer_a !== null) pointer_a -= 1
                if (pointer_b !== null) pointer_b += 1

            } while ( true )
            
            checkedRows.set(`${originTable.side}-${originTable.row}`, true)
            if (!result) continue

            const [destinyTableId, destinySeat] = result
            const table = tablesInRow.find(({ id }) => id === destinyTableId)
            const tableInBooking = booking.getTable(destinyTableId)
            if (!table) throw new Error(`table not found in row`)

            const childTicket = products.find(product => 
                product.type === 'ticket' &&
                product.category === 'children' &&
                product.zone === table.zone
            )
            if (!childTicket) throw new Error(`child ticket not found, zone: ${table.zone} products length: ${products.length}`)

            const surcharge = products.find(product => product.name === SURCHARGE_NAME) 
            if (!surcharge) throw new Error('surcharge not found')

            const tableToHandle = {
                id: table.id,
                name: table.name,
                zone: table.zone,
                pax: table.pax,
                occupied: (tableInBooking?.occupied ?? 0) + 1,
                seats: [...(tableInBooking?.seats ?? []), destinySeat]
            }
            
            booking.removeSurcharge(surcharge)
            booking.handleTable(tableToHandle)
            booking.unselectProducts()
            booking.handleProduct('add', childTicket, result as LayoutLocation).render()
            break;
        }

        if (!result) throw new Error('could not find any adjacent seat')
    }

    const zoomOut = () => applyZoom(Math.max(0.1, zoom.current - (0.25 * zoom.current)))
    const zoomIn = () => applyZoom(Math.min(3, zoom.current + (0.25 * zoom.current)))
    const goToStartPosition = () => focusRect()
    const removeFocusStyles = () => container.current?.removeAttribute('data-focusing')
    const getCurrentlyFocusing = () => container.current?.getAttribute('data-focusing') ?? undefined
    const searchForIndividualTables = () => focusRect({rect: 'cv', dataFocusing: 'individuals'})
    const searchForDoubleTables = () => focusRect({rect: 'cv', dataFocusing: 'doubles'})
    const searchForAccessibleTables = () => focusRect({rect: 'cv', dataFocusing: 'accessibles'})

    useEffect(() => {

        if (dialogHandles.size === 0) return

        const { shiftShown } = load('localStorage', 'booking-instructions', DEFAULT_BOOKING_INSTRUCTIONS)
        if (!shiftShown && booking.step === 'shift') {
            dialog.openDialog({
                dialog: 'select-shift-first',
                className: 'select-shift-first w-100 h-100 df aic jcc'
            })
        }

        /* Every time shifts (when shift step is loaded) or
         * Bookingdata.eventId change get the corresponding table layout */
        /* case 1: we have eventId in booking */
        if (booking.eventId !== 0) getTableLayout(booking.eventId)
        /* case 2: we do not have eventId in booking but we have shifts */
        else if (shifts[0]) getTableLayout(shifts[0].eventId)
        
        if (lastBookingStep.current !== booking.step) focusRect()

        lastBookingStep.current = booking.step

    }, [forcedRenders, shifts, dialogHandles])

    useEffect(() => {
        updateTableMapHandle({
            focusRect, 
            goToStartPosition, 
            zoomOut, 
            zoomIn,
            getEventOccupation,
            searchForIndividualTables,
            getCurrentlyFocusing,
            removeFocusStyles,
            searchForDoubleTables,
            searchForAccessibleTables,
            selectAdjacentChildrenSeat
        })
    }, [layout, products])
    
    useEffect(() => { 
        focusRect() 
         
    }, [screen, layout])

    if (booking.step === 'products' && screen.width <= SCREEN_BREAKPOINT_2) return null

    return (

        <div 
            aria-hidden={booking.step === 'tickets' ? 'false' : 'true'}
            ref={container}
            className={`table-map h-100 w-100 p-r`}
            data-applied-rotation={layout.appliedRotation}
            data-focusing="individuals"
            style={{
                '--r': `${layout.appliedRotation}deg`,
                '--center-x': `${(LAYOUT_ORIGIN_X)}px`,
                '--center-y': `${(LAYOUT_ORIGIN_Y)}px`,
            } as CustomCSSProperties}
        >

            <Dialog 
                id="table-map-dialog" 
                availableDialogs={[
                    'select-shift-first', 
                    'loading'
                ]}
            />

            <div 
                ref={zoomControler}
                className="table-map-zoom-controller p-a"
                style={{left: `${CONTAINER_CENTER_X}px`, top: `${CONTAINER_CENTER_Y}px`}}
            >
                <ul 
                    id="table-layout"
                    className="layout p-a" 
                    aria-label={_('3zo_selec_z')}
                    tabIndex={0}
                    role="region"
                >
                    {
                        Object.entries(layout.processedTables.reduce((groupedTables, table) => {

                            //group tables by zones -> {zoneName: [tables...], ...}
                            if (!groupedTables[table.zone].find(includedTable => includedTable.id === table.id)) groupedTables[table.zone].push(table)
                            return groupedTables
                        
                        }, ({zv: [], za: [], zb: [], zc: [], za2: []}) as GroupedTables)).map(([zone, tables]) => {

                            if (tables.length === 0) return null

                            const tableZone = zone as SafeZone 
                            const zoneName = getZoneName('long', tableZone, booking.journey)
                            const ticket = products.find(product => product.type === 'ticket' && product.category === 'adult' && product.zone === tableZone)
                            const parsedPrice = ticket ? parseNum('EUR', ticket.price) : ''

                            return (
                                <li 
                                    id={zone} 
                                    key={zone} 
                                    aria-label={`${_('0ar_table_i')} ${zoneName}, ${parsedPrice}. ${bookedZones[tableZone]} ${_('0ar_selec__')}`} 
                                    tabIndex={0} 
                                    role="group"
                                >
                                    <ul id={`${zone}-tables`}>
                                        { tables.map(table => 
                                            <Table 
                                                key={table.id} 
                                                table={table} 
                                                onTableClick={onSeatClick} 
                                            />
                                        )}
                                    </ul>
                                </li>
                            )
                        })
                    }
                    <li 
                        className="p-a layout-bg pe-n"
                        style={{
                            '--left': `${layout.rects.cv.lt.x}px`,
                            '--top': `${layout.rects.cv.lt.y}px`,
                            '--height': `${layout.rects.cv.height}px`,
                            '--width': `${layout.rects.cv.width}px`,
                        } as CustomCSSProperties}>
                    </li>
                    <Stage />
                    {   !rumba.isProd() && Object.values(layout.rects).map((rect, index) => {

                            return (
                                <div
                                    key={index}
                                    style={{'left': `${rect.lt.x}px`, 'top': `${rect.lt.y}px`, 'height': `${rect.height}px`, 'width': `${rect.width}px`,}} 
                                    className="table-map-rule">
                                </div>
                            )
                        })

                    }
                </ul>
            </div>
            {/* CONTROLS --------------------------------------  */}
            <div className="controls df fd-c" aria-hidden>
                <button tabIndex={-1} onClick={zoomIn} className="zoom-in button b-0 p-0 r3 white"><Icon icon="plus"/></button>
                {/* <button tabIndex={-1} onClick={goToStartPosition} className="reset button b-0 p-0 r3 white"><Icon icon="circle-2"/></button> */}
                <button tabIndex={-1} onClick={zoomOut} className="zoom-out button b-0 p-0 white"><Icon icon="minus"/></button>
            </div>
            {/* as the event controller is behind the tables, clicking a table won't move the layout */}
            <div aria-hidden id="event-controller" className="h-100 w-100" onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp} onTouchMove={handleTouchMove} onTouchStart={handleTouchStart} onTouchEnd={handleMouseUp}></div>
        </div>
    )
}

